(() => {
  if (!window.customElements || customElements.get('divano-pdp-pairing')) return;
  const impressions = new Set();
  const blocks = new Map();
  const layouts = { a: 'compact', b: 'inline', c: 'expand' };

  function assignment(setting, designMode) {
    if (setting !== 'split') {
      const variant = ({ compact: 'a', inline: 'b', expand: 'c' })[setting] || 'a';
      return { layout: layouts[variant], variant, assignment: 'fixed' };
    }
    if (designMode) return { layout: 'compact', variant: 'default', assignment: 'default' };
    try {
      let variant = document.cookie.match(/(?:^|;\s*)divano_pb=([abc])(?:;|$)/)?.[1];
      if (!variant) variant = 'abc'[Math.floor(Math.random() * 3)];
      document.cookie = `divano_pb=${variant}; Max-Age=7776000; path=/; SameSite=Lax`;
      if (!document.cookie.match(new RegExp(`(?:^|;\\s*)divano_pb=${variant}(?:;|$)`))) throw Error('cookie blocked');
      return { layout: layouts[variant], variant, assignment: 'split' };
    } catch {
      return { layout: 'compact', variant: 'default', assignment: 'default' };
    }
  }

  function publish(name, data) {
    let tries = 0;
    (function attempt() {
      const bus = window.Shopify && window.Shopify.analytics;
      if (bus && typeof bus.publish === 'function') {
        try { bus.publish(name, data); } catch {  }
        return;
      }
      if (++tries < 20) setTimeout(attempt, 250);
    })();
  }

  function money(cents) {
    const format = window.Shopify?.money_format || '${{amount}}';
    const prefix = format.replace(/\{\{\s*amount\s*\}\}/g, '').replace(/[^$£€¥]/g, '').trim() || '$';
    const [whole, fraction] = (cents / 100).toFixed(2).split('.');
    return `${prefix}${whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}.${fraction}`;
  }
  const moneyShort = (cents) => cents % 100 === 0 ? money(cents).replace(/\.00$/, '') : money(cents);
  function bundleAllocated(item, rate) {
    const unitPrice = Number(item?.original_price || (item?.original_line_price / item?.quantity));
    const minimum = Math.floor(unitPrice * rate / 10000);
    const titlePrefix = 'Bundle: ' + (rate / 100) + '% off';
    return Number.isFinite(minimum) && minimum > 0 && (item?.line_level_discount_allocations || []).some((allocation) => {
      const title = allocation.discount_application?.title || '';
      const suffix = title.startsWith(titlePrefix) ? title.slice(titlePrefix.length) : null;
      const application = allocation.discount_application;
      return application?.value_type === 'percentage' && Math.round(Number(application.value) * 100) === rate &&
        application?.target_type === 'line_item' &&
        suffix !== null && /^(?: [0-9]+ of [0-9]+)?(?: · best of [0-9]+ offers)?$/.test(suffix) &&
        Number(allocation.amount) >= minimum;
    });
  }
  function bothAllocated(cart, mainId, mainVariants, partnerId, partnerVariant, rate) {
    const items = cart?.items || [];
    const main = items.filter((item) => String(item.product_id) === String(mainId));
    const partner = items.filter((item) => String(item.product_id) === String(partnerId));
    return main.length === 1 && partner.length === 1 && mainVariants.includes(String(main[0].variant_id)) && String(partner[0].variant_id) === String(partnerVariant) &&
      Number(main[0].quantity || 1) >= 1 && Number(partner[0].quantity || 1) >= 1 &&
      bundleAllocated(main[0], rate) && bundleAllocated(partner[0], rate);
  }

  async function timedJson(url, options = {}) {
    const controller = new AbortController();
    let timer;
    try {
      return await Promise.race([
        (async () => {
          const response = await fetch(url, { ...options, signal: controller.signal });
          let body = null;
          try { body = await response.json(); } catch {  }
          return { ok: response.ok, status: response.status, body };
        })(),
        new Promise((_, reject) => { timer = setTimeout(() => { controller.abort(); reject(Error('timeout')); }, 10000); }),
      ]);
    } finally { clearTimeout(timer); }
  }

  class Pairing extends HTMLElement {
    static get observedAttributes() { return ['data-layout', 'data-variant', 'data-assignment']; }

    attributeChangedCallback() {
      if (this._assigned && !this._restoringAssignment) {
        this._restoringAssignment = true;
        Object.assign(this.dataset, this._assigned);
        this._restoringAssignment = false;
      }
    }

    connectedCallback() {
      if (this.closest('.quick-add-modal__content')) { this._inert = true; return; }
      const key = `${this.dataset.productId}:${this.dataset.blockId}`;
      this._shared = blocks.get(key) || { generation: 0, adding: false, unresolved: false, current: null };
      blocks.set(key, this._shared);
      this._shared.current = this;
      const result = assignment(this.dataset.layoutSetting, this.dataset.designMode === 'true');
      this._assigned = result;
      Object.assign(this.dataset, result);
      this._onClick = (event) => this.onClick(event);
      this._onChange = (event) => this.onChange(event);
      this._onKey = (event) => this.onKey(event);
      this._onCart = (event) => this.onCart(event);
      this._onReturn = () => { if (document.visibilityState !== 'hidden') this.checkVisibleCart(); };
      this._onVariant = (event) => this.onVariant(event);
      this._onPending = (event) => { if (this.ownVariantEvent(event)) { this._shared.unresolved = true; this._formUnresolved = true; this.bump(); this.pending(true); } };
      this._onForm = (event) => { if (String(event.target?.closest?.('product-form-component')?.dataset.productId) === this.dataset.productId) this.formChanged(); };
      this.addEventListener('click', this._onClick);
      this.addEventListener('change', this._onChange);
      this.addEventListener('keydown', this._onKey);
      document.addEventListener('cart:update', this._onCart);
      document.addEventListener('visibilitychange', this._onReturn);
      window.addEventListener('focus', this._onReturn);
      document.addEventListener('variant:update', this._onVariant);
      document.addEventListener('variant:selected', this._onPending);
      document.addEventListener('divano:variant:select', this._onPending);
      document.addEventListener('change', this._onForm);
      this.setAttribute('data-ready', 'true');
      if (this.dataset.lifecycleHost === 'true') return;
      const form = [...document.querySelectorAll('product-form-component')].find((node) => String(node.dataset.productId) === this.dataset.productId);
      this._formUnresolved = Boolean(form && !form.querySelector('input[name="id"]')?.value);
      this._shared.unresolved = this._shared.unresolved || this._formUnresolved;
      this.recalc();
      this.readCart();
      if ('IntersectionObserver' in window) {
        this._observer = new IntersectionObserver((entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          this.impression();
          this._observer.disconnect();
        }, { threshold: 0.1 });
        this._observer.observe(this);
      } else this.impression();
    }

    disconnectedCallback() {
      if (this._inert) return;
      this.restoreOutside();
      this.unlockScroll();
      this.removeEventListener('click', this._onClick);
      this.removeEventListener('change', this._onChange);
      this.removeEventListener('keydown', this._onKey);
      document.removeEventListener('cart:update', this._onCart);
      document.removeEventListener('visibilitychange', this._onReturn);
      window.removeEventListener('focus', this._onReturn);
      document.removeEventListener('variant:update', this._onVariant);
      document.removeEventListener('variant:selected', this._onPending);
      document.removeEventListener('divano:variant:select', this._onPending);
      document.removeEventListener('change', this._onForm);
      this._observer?.disconnect();
      this._request?.abort();
    }

    event(name, extra = {}) {
      publish(name, { product_id: this.dataset.productId, variant: this.dataset.variant, assignment: this.dataset.assignment, ...extra });
    }

    ownVariantEvent(event) {
      const eventScope = event.target instanceof Element ? event.target.closest('.product-details') : null;
      const ownScope = this.closest('.product-details');
      if (eventScope && ownScope && eventScope !== ownScope) return false;
      const productId = event.detail?.data?.productId;
      return !productId || String(productId) === this.dataset.productId || Boolean(event.detail?.data?.newProduct);
    }

    impression() {
      const key = `${location.pathname}:${this.dataset.productId}`;
      if (impressions.has(key)) return;
      impressions.add(key);
      this.event('divano_pairing_impression', { partners: Number(this.dataset.partners), claim: this.dataset.claim === 'both' ? 'both' : 'none' });
    }

    formVariantId() {
      const form = [...document.querySelectorAll('product-form-component')].find((node) => String(node.dataset.productId) === this.dataset.productId);
      return form?.querySelector('input[name="id"]')?.value || null;
    }

    hasEmptyFormId() {
      const form = [...document.querySelectorAll('product-form-component')].find((node) => String(node.dataset.productId) === this.dataset.productId);
      return Boolean(form && !form.querySelector('input[name="id"]')?.value);
    }

    cartSignature(cart) {
      return (cart?.items || []).map((item) => `${item.variant_id ?? ''}:${item.quantity ?? ''}:${item.original_price ?? ''}:${(item.line_level_discount_allocations || [])
        .map((allocation) => `${String(allocation.discount_application?.title || '').replaceAll(',', '&#44;').replaceAll('|', '&#124;')}=${allocation.amount}`).join(',')}`).sort().join('|');
    }

    async checkVisibleCart() {
      if (!this.isConnected || this._shared.adding || this.dataset.lifecycleHost === 'true') return;
      const generation = this._shared.generation;
      try {
        const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
        if (!response.ok || !this.isConnected || generation !== this._shared.generation) return;
        const cart = await response.json();
        if (!this.isConnected || generation !== this._shared.generation) return;
        if (this.cartSignature(cart) !== this.dataset.cartSnapshot || this._needsRefresh) {
          this.pending(true);
          await this.refresh('visibility');
        } else {
          this.useCartMode(cart);
          const saving = this.querySelector('[data-pairing-confirmation-saving]');
          if (saving && saving.textContent === this.dataset.appliedCopy) {
            const proof = this._proof;
            if (!proof || !bothAllocated(cart, this.dataset.productId, proof.mainVariants, proof.partnerId, proof.variantId, proof.rate)) {
              saving.textContent = this.dataset.cartCopy;
            }
          }
        }
      } catch {  }
    }

    invalidateRefresh() {
      this._request?.abort();
      this._request = null;
    }

    bump() {
      this._shared.generation++;
      this.invalidateRefresh();
      return this._shared.generation;
    }

    selected() {
      const checked = this.querySelector('[data-pairing-row] input[type=radio]:checked');
      return checked?.closest('[data-pairing-row]') || null;
    }

    recalc() {
      const row = this.selected();
      const button = this.querySelector('[data-pairing-add]');
      if (!button) return;
      if (this._shared.refreshCapped) this.showCappedError();
      if (!row) {
        button.disabled = true;
        button.setAttribute('aria-disabled', 'true');
        this.querySelector('[data-pairing-total]').textContent = '';
        return;
      }
      const main = Number(this.dataset.mainPrice);
      const partner = Number(row.dataset.price);
      const save = this.dataset.claim === 'both' ? Math.floor(Number(this.dataset.claimMainPrice || main) * Number(this.dataset.rate) / 10000) + Number(row.dataset.save) : 0;
      this.querySelector('[data-pairing-total]').textContent = this.dataset.mainInCart === 'true' || this.dataset.claim !== 'both' ? '' : money(main + partner - save);
      const pairName = this.querySelector('[data-pairing-pair-name]');
      if (pairName) pairName.textContent = this.dataset.mainShort + ' + ' + (row.dataset.partnerShort || row.dataset.partnerTitle);
      button.textContent = this.dataset.mainInCart === 'true' || this.dataset.partnerOnly === 'true' ? row.dataset.partnerButton : this.dataset.addPair;
      button.disabled = this._shared.adding || this._shared.unresolved || this._shared.refreshCapped || this._formUnresolved === true;
      button.setAttribute('aria-disabled', String(button.disabled));
      const saving = row.querySelector('[data-row-saving]');
      if (saving && this.dataset.claim === 'both') saving.textContent = this.dataset.rowTemplate.replace('[amount]', moneyShort(save));
    }

    async readCart() {
      await this.checkVisibleCart();
    }

    useCartMode(cart) {
      this.dataset.mainInCart = String((cart.items || []).some((item) => String(item.product_id) === this.dataset.productId));
      this.recalc();
    }

    formChanged() {
      const value = this.formVariantId();
      if (!value) { this._shared.unresolved = true; this._formUnresolved = true; this.bump(); this.pending(true); return; }
      const wasUnresolved = this._shared.unresolved || this._formUnresolved;
      if (wasUnresolved || String(value) !== this.dataset.mainVariantId) this.bump();
      this._shared.unresolved = false;
      this._formUnresolved = false;
      if (wasUnresolved || String(value) !== this.dataset.mainVariantId) {
        this.dataset.mainVariantId = String(value);
        this.pending(true);
        this.refresh('variant');
      } else this.recalc();
    }

    showOpen(restore = false) {
      this.dataset.open = 'true';
      this.querySelector('[data-pairing-open]')?.setAttribute('aria-expanded', 'true');
      if (this.dataset.layout === 'compact') {
        this.inertOutside();
        this.lockScroll();
        const list = this.querySelector('[data-pairing-list]');
        list.setAttribute('role', 'dialog');
        list.setAttribute('aria-modal', 'true');
        if (!restore) this.querySelector('[data-pairing-close]')?.focus();
      } else if (!restore) this.selected()?.querySelector('input[type=radio]')?.focus();
    }

    open() {
      if (this.dataset.open === 'true') return;
      this._returnFocus = document.activeElement;
      this.showOpen();
      this.event('divano_pairing_open');
    }

    close() {
      this.dataset.open = 'false';
      this.querySelector('[data-pairing-open]')?.setAttribute('aria-expanded', 'false');
      const list = this.querySelector('[data-pairing-list]');
      list.setAttribute('role', 'group');
      list.removeAttribute('aria-modal');
      this.restoreOutside();
      this.unlockScroll();
      this._returnFocus?.focus();
    }

    inertOutside() {
      this._inertNodes = [];
      let path = this.querySelector('[data-pairing-list]') || this;
      while (path?.parentElement) {
        for (const sibling of path.parentElement.children) {
          if (sibling === path) continue;
          this._inertNodes.push([sibling, sibling.inert]);
          sibling.inert = true;
        }
        path = path.parentElement;
        if (path === document.body) break;
      }
    }

    restoreOutside() {
      for (const [node, wasInert] of this._inertNodes || []) node.inert = wasInert;
      this._inertNodes = [];
    }

    lockScroll() {
      if (this._scrollBefore !== undefined) return;
      this._scrollBefore = { root: document.documentElement.style.overflow, body: document.body.style.overflow };
      document.documentElement.style.overflow = 'hidden';
      document.body.style.overflow = 'hidden';
    }

    unlockScroll() {
      if (this._scrollBefore === undefined) return;
      document.documentElement.style.overflow = this._scrollBefore.root;
      document.body.style.overflow = this._scrollBefore.body;
      this._scrollBefore = undefined;
    }

    onKey(event) {
      if (event.key === 'Escape' && this.dataset.layout === 'compact' && this.dataset.open === 'true') {
        event.preventDefault(); this.close(); return;
      }
      if (event.key !== 'Tab' || this.dataset.layout !== 'compact' || this.dataset.open !== 'true') return;
      const nodes = [...this.querySelector('[data-pairing-list]').querySelectorAll('button:not([disabled]),input:not([disabled]),select:not([disabled]),a[href]')].filter((node) => node.getClientRects().length);
      if (!nodes.length) return;
      if (event.shiftKey && document.activeElement === nodes[0]) { event.preventDefault(); nodes.at(-1).focus(); }
      else if (!event.shiftKey && document.activeElement === nodes.at(-1)) { event.preventDefault(); nodes[0].focus(); }
    }

    onChange(event) {
      const row = event.target.closest?.('[data-pairing-row]');
      if (!row) return;
      if (event.target.matches('input[type=radio]')) this.event('divano_pairing_select', { partner_product_id: row.dataset.partnerProductId });
      if (event.target.matches('[data-partner-variant]')) {
        const option = event.target.selectedOptions[0];
        row.dataset.variantId = option.value;
        row.dataset.price = option.dataset.price;
        row.dataset.save = option.dataset.save;
        row.querySelector('[data-partner-link]')?.setAttribute('href', row.dataset.partnerUrl + '?variant=' + encodeURIComponent(option.value));
        this.updateRow(row);
      }
      this.recalc();
    }

    updateRow(row) {
      const price = row.querySelector('[data-row-price]');
      if (price) price.textContent = '+ ' + money(Number(row.dataset.price));
      const saving = row.querySelector('[data-row-saving]');
      if (saving && this.dataset.claim === 'both') {
        const mainSave = Math.floor(Number(this.dataset.claimMainPrice || this.dataset.mainPrice) * Number(this.dataset.rate) / 10000);
        saving.textContent = this.dataset.rowTemplate.replace('[amount]', moneyShort(mainSave + Number(row.dataset.save)));
      }
    }

    onClick(event) {
      const view = event.target.closest('[data-pairing-view]');
      if (view) { this.event('divano_pairing_partner_view', { partner_product_id: view.closest('[data-pairing-row]')?.dataset.partnerProductId }); return; }
      if (event.target === this && this.dataset.layout === 'compact' && this.dataset.open === 'true') { this.close(); return; }
      if (event.target.closest('[data-pairing-open]')) {
        if (this.dataset.layout === 'expand' && this.dataset.open === 'true') this.close();
        else this.open();
        return;
      }
      if (event.target.closest('[data-pairing-close]')) { this.close(); return; }
      if (event.target.closest('[data-pairing-more]')) {
        this.dataset.showAll = 'true';
        event.target.setAttribute('aria-expanded', 'true');
        this.querySelector('[data-pairing-row][data-extra] input')?.focus();
        return;
      }
      if (event.target.closest('[data-pairing-another]')) {
        const available = [...this.querySelectorAll('[data-pairing-row]')].filter((row) => !this._cartPartnerIds?.has(row.dataset.partnerProductId));
        if (!available.length) return;
        this.querySelector('[data-pairing-confirmation]').hidden = true;
        this.querySelector('[data-pairing-offer]').hidden = false;
        this.querySelectorAll('[data-pairing-row]').forEach((row) => { row.hidden = !available.includes(row); row.querySelector('input[type=radio]').checked = false; });
        available.forEach((row, index) => { if (index < 3) row.removeAttribute('data-extra'); else row.setAttribute('data-extra', ''); });
        if (available[0]) available[0].querySelector('input[type=radio]').checked = true;
        this.dataset.claim = 'none';
        this.dataset.partnerOnly = 'true';
        this.querySelector('[data-pairing-heading]').textContent = this.dataset.neutralHeading;
        this.querySelector('[data-pairing-list-heading]').textContent = this.dataset.neutralHeading;
        this.querySelector('[data-pairing-claim]').textContent = this.dataset.cartCopy;
        this.recalc();
        available[0].querySelector('input[type=radio]')?.focus();
        return;
      }
      if (event.target.closest('[data-pairing-add]')) this.add();
    }

    pending(on) {
      this.dataset.pending = String(on);
      if (this.dataset.lifecycleHost === 'true') return;
      if (on) {
        this.dataset.claim = 'none';
        this.querySelector('[data-pairing-claim]').textContent = this.dataset.cartCopy;
        this.querySelector('[data-pairing-heading]').textContent = this.dataset.neutralHeading;
        this.querySelector('[data-pairing-list-heading]').textContent = this.dataset.neutralHeading;
        this.querySelectorAll('[data-row-saving]').forEach((node) => { node.textContent = ''; });
        this.querySelectorAll('[data-pairing-subline]').forEach((node) => { node.textContent = ''; });
        const saving = this.querySelector('[data-pairing-confirmation-saving]');
        if (saving) saving.textContent = this.dataset.cartCopy;
      }
      this.recalc();
    }

    makeHost() {
      this.restoreOutside();
      this.unlockScroll();
      this.dataset.open = 'false';
      this.querySelector('[data-pairing-open]')?.setAttribute('aria-expanded', 'false');
      const form = [...document.querySelectorAll('product-form-component')].find((node) => String(node.dataset.productId) === this.dataset.productId);
      (form?.querySelector('[type="submit"]') || form?.querySelector('button'))?.focus();
      this._observer?.disconnect();
      this.replaceChildren();
      this.hidden = true;
      this.dataset.lifecycleHost = 'true';
      this.dataset.claim = 'none';
    }

    async onVariant(event) {
      if (!this.isConnected || !this.ownVariantEvent(event)) return;
      const next = event.detail?.resource?.id;
      if (event.detail?.data?.newProduct) { this.remove(); return; }
      if (!next) { this.bump(); this._shared.unresolved = true; this._formUnresolved = true; this.pending(true); return; }
      for (const input of document.querySelectorAll('product-form-component input[name="id"]')) {
        if (String(input.closest('product-form-component')?.dataset.productId || '') !== this.dataset.productId) continue;
        if (!input.value) { this.bump(); this._shared.unresolved = true; this._formUnresolved = true; this.pending(true); return; }
        if (String(input.value) !== String(next)) return;
        break;
      }
      this.bump();
      this.dataset.mainVariantId = String(next);
      this._shared.unresolved = false;
      this._formUnresolved = false;
      this.pending(true);
      await this.refresh('variant');
    }

    async onCart(event) {
      if (this._shared.adding && event.detail?.sourceId === `divano-pairing:${this.dataset.blockId}`) return;
      const generation = this.bump();
      this.pending(true);
      try {
        const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
        if (response.ok && this.isConnected && generation === this._shared.generation) {
          const cart = await response.json();
          if (this.isConnected && generation === this._shared.generation && this.cartSignature(cart) === this.dataset.cartSnapshot) this.useCartMode(cart);
        }
      } catch {  }
      if (!this.isConnected || generation !== this._shared.generation) return;
      await this.refresh('cart');
    }

    showCappedError() {
      this.querySelector('[data-pairing-status]')?.replaceChildren(document.createTextNode(this.dataset.errorCopy));
      const visible = this.querySelector('[data-pairing-error]');
      if (visible) { visible.textContent = this.dataset.errorCopy; visible.hidden = false; }
    }

    async refresh(kind = 'direct') {
      const failures = this._shared.refreshFailures ||= {};
      if ((failures[kind] || 0) >= 3) {
        this._shared.refreshCapped = true;
        this.pending(true);
        this.showCappedError();
        return;
      }
      const generation = this.bump();
      this._request?.abort();
      const controller = new AbortController();
      this._request = controller;
      const chosen = this.selected();
      const choice = chosen ? { product: chosen.dataset.partnerProductId, variant: chosen.dataset.variantId } : null;
      const expanded = this.dataset.showAll === 'true';
      const wasOpen = this.dataset.open === 'true';
      const focused = this.contains(document.activeElement) ? document.activeElement : null;
      const focusKind = focused?.matches('[data-pairing-add]') ? 'add' : focused?.matches('[data-pairing-more]') ? 'more' : focused?.matches('[data-partner-variant]') ? 'variant' : focused?.matches('input[type=radio]') ? 'radio' : null;
      const focusedProduct = focused?.closest('[data-pairing-row]')?.dataset.partnerProductId;
      const url = `${this.dataset.productUrl}?variant=${encodeURIComponent(this.dataset.mainVariantId)}&section_id=${encodeURIComponent(this.dataset.sectionId)}`;
      const watchdog = setTimeout(() => {
        if (controller !== this._request || !this.isConnected || generation !== this._shared.generation) return;
        controller.abort();
        this._needsRefresh = true;
        this.pending(false);
      }, 30000);
      try {
        const response = await fetch(url, { signal: controller.signal });
        if (!response.ok) throw Error(`HTTP ${response.status}`);
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        if (controller !== this._request || !this.isConnected || generation !== this._shared.generation) return;
        const fresh = [...doc.querySelectorAll('divano-pdp-pairing')].find((node) => node.dataset.blockId === this.dataset.blockId);
        if (fresh) {
          const formVariant = this.formVariantId();
          if (String(fresh.dataset.mainVariantId) !== String(this.dataset.mainVariantId) || (formVariant && String(fresh.dataset.mainVariantId) !== String(formVariant))) {
            this._needsRefresh = true;
            this.pending(false);
            return;
          }
          if (this._shared.unresolved || this.hasEmptyFormId()) {
            this.pending(true);
            this._needsRefresh = true;
            return;
          }
          const focusStillHere = focused && document.activeElement === focused;
          this.replaceWith(fresh);
          this._shared.needsReconcile = false;
          if (this._shared.adding) fresh.pending(true);
          const previousRow = choice && [...fresh.querySelectorAll('[data-pairing-row]')].find((item) => item.dataset.partnerProductId === choice.product);
          const previousSelect = previousRow?.querySelector('[data-partner-variant]');
          const canPreserve = previousRow && previousRow.dataset.inCart !== 'true' &&
            ((!previousSelect && previousRow.dataset.variantId === choice.variant) || (previousSelect && [...previousSelect.options].some((option) => option.value === choice.variant)));
          if (this.dataset.lifecycleHost !== 'true' && canPreserve) fresh.querySelectorAll('[data-pairing-row] input[type=radio]').forEach((input) => { input.checked = false; });
          if (choice) {
            const row = previousRow;
            if (canPreserve) {
              const select = row.querySelector('[data-partner-variant]');
              if ((!select && row.dataset.variantId === choice.variant) || (select && [...select.options].some((option) => option.value === choice.variant))) {
                if (select) {
                  select.value = choice.variant;
                  const option = select.selectedOptions[0];
                  row.dataset.variantId = option.value;
                  row.dataset.price = option.dataset.price;
                  row.dataset.save = option.dataset.save;
                  row.querySelector('[data-partner-link]')?.setAttribute('href', row.dataset.partnerUrl + '?variant=' + encodeURIComponent(option.value));
                  fresh.updateRow(row);
                }
                row.querySelector('input[type=radio]').checked = true;
              }
            }
          }
          fresh.dataset.showAll = String(expanded);
          fresh.querySelector('[data-pairing-more]')?.setAttribute('aria-expanded', String(expanded));
          if (wasOpen && fresh.dataset.layout !== 'inline' && fresh.dataset.lifecycleHost !== 'true') {
            fresh._returnFocus = fresh.querySelector('[data-pairing-open]');
            fresh.showOpen(true);
          }
          fresh.recalc();
          if (focusStillHere && focusKind === 'add') fresh.querySelector('[data-pairing-add]')?.focus();
          if (focusStillHere && focusKind === 'more') fresh.querySelector('[data-pairing-more]')?.focus();
          if (focusStillHere && focusKind === 'radio') fresh.selected()?.querySelector('input[type=radio]')?.focus();
          if (focusStillHere && focusKind === 'variant') [...fresh.querySelectorAll('[data-pairing-row]')].find((row) => row.dataset.partnerProductId === focusedProduct)?.querySelector('[data-partner-variant]')?.focus();
          if (focusStillHere && wasOpen && !focusKind) fresh.querySelector('[data-pairing-close]')?.focus();
        } else if (doc.getElementById(`shopify-section-${this.dataset.sectionId}`)) { this.makeHost(); }
        else throw Error('section_missing');
      } catch (error) {
        if (controller === this._request && this.isConnected) {
           failures[kind] = (failures[kind] || 0) + 1;
           this._needsRefresh = true;
           this.pending(false);
           if (failures[kind] >= 3) {
             this._shared.refreshCapped = true;
             this.showCappedError();
             this.recalc();
           }
        }
      } finally {
        clearTimeout(watchdog);
      }
    }

    async add() {
      if (this._shared.adding || this._shared.unresolved || this._formUnresolved) return;
      if (this.hasEmptyFormId()) { this.formChanged(); return; }
      const row = this.selected();
      if (!row) return;
      const addGeneration = this._shared.generation;
      const partnerId = row.dataset.partnerProductId;
      const initialClaim = this.dataset.claim === 'both';
      let mode;
      let items;
      let attemptPublished = false;
      this.querySelector('[data-pairing-status]').textContent = '';
      this._shared.adding = true;
      this.pending(true);
      let reason = 'network';
      let ok = false;
      try {
        const beforeResponse = await timedJson('/cart.js');
        if (beforeResponse.status === 429) throw Error('rate_limited');
        if (!beforeResponse.ok) throw Error('cart_unavailable');
        const before = beforeResponse.body;
        if (!Array.isArray(before.items)) throw Error('cart_unavailable');
        if (this._shared.needsReconcile) {
          reason = 'cart_changed';
          this.refresh('preflight');
          return;
        }
        if (addGeneration !== this._shared.generation || this._shared.unresolved) throw Error('cart_changed');
        const mainBefore = before.items.filter((item) => String(item.product_id) === this.dataset.productId);
        mode = mainBefore.length || this.dataset.partnerOnly === 'true' ? 'partner_only' : 'pair';
        items = mode === 'pair' ? [{ id: Number(this.dataset.mainVariantId), quantity: 1 }, { id: Number(row.dataset.variantId), quantity: 1 }] : [{ id: Number(row.dataset.variantId), quantity: 1 }];
        this.event('divano_pairing_add', { mode, partner_product_id: partnerId });
        attemptPublished = true;
        if (this.cartSignature(before) !== this.dataset.cartSnapshot) {
          reason = 'cart_changed';
          this.invalidateRefresh();
          this.pending(true);
          this.refresh('preflight');
          return;
        }
        this.useCartMode(before);
        if ((before.items || []).length + items.length > 500) throw Error('cart_line_limit');
        const sections = [...new Set([...document.querySelectorAll('cart-items-component')].map((el) => el.dataset.sectionId).filter(Boolean))];
        if (mode === 'pair' && String(this.formVariantId() || '') !== this.dataset.mainVariantId) {
          const formId = this.formVariantId();
          if (formId) {
            this.dataset.mainVariantId = String(formId);
            this.refresh('variant');
          } else { this.bump(); this._shared.unresolved = true; this._formUnresolved = true; }
          throw Error('variant_changed');
        }
        const addResponse = await timedJson('/cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items, sections: sections.join(',') }) });
        if (addResponse.status === 429) throw Error('rate_limited');
        if (!addResponse.ok) throw Error(`HTTP ${addResponse.status}`);
        this.bump();
        const addBody = addResponse.body || {};
        let postAddUnconfirmed = false;
        let cart = {};
        try {
          const cartResponse = await timedJson('/cart.js');
          if (cartResponse.ok) cart = cartResponse.body || {};
        } catch (error) { if (error.message === 'timeout') postAddUnconfirmed = true;  }
        document.dispatchEvent(new CustomEvent('cart:update', { bubbles: true, detail: { resource: cart, sourceId: `divano-pairing:${this.dataset.blockId}`, data: { source: 'divano-pairing', productId: this.dataset.productId, itemCount: cart.item_count, sections: addBody.sections || {} } } }));
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { resource: cart, sourceId: `divano-pairing:${this.dataset.blockId}`, data: { source: 'divano-pairing', sections: addBody.sections || {} } } }));
        const tag = this.dataset.assignment === 'fixed' ? `fixed-${this.dataset.variant}` : this.dataset.variant;
        let tagged = false;
        for (let attempt = 0; attempt < 3 && !tagged; attempt++) {
          if (attempt) await new Promise((resolve) => setTimeout(resolve, 100 * 2 ** (attempt - 1)));
          try {
            const update = await timedJson('/cart/update.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ attributes: { _pairing_variant: tag } }) });
            tagged = update.ok;
          } catch (error) { if (error.message === 'timeout') { postAddUnconfirmed = true; break; }  }
        }
        let fresh = null;
        const proofGeneration = this._shared.generation;
        try {
          const freshResponse = await timedJson('/cart.js');
          if (freshResponse.ok) fresh = freshResponse.body;
        } catch (error) { if (error.message === 'timeout') postAddUnconfirmed = true;  }
        if (postAddUnconfirmed) this._shared.needsReconcile = true;
        const proofCurrent = proofGeneration === this._shared.generation && !this._shared.unresolved;
        const live = this._shared.current?.isConnected ? this._shared.current : this;
        tagged = tagged && fresh?.attributes?._pairing_variant === tag;
        if (!live.querySelector('[data-pairing-confirmation]')) {
          ok = true;
          reason = postAddUnconfirmed ? 'added_unconfirmed' : tagged ? 'added' : 'added_without_tag';
          return;
        }
        if (proofCurrent && fresh) live.dataset.cartSnapshot = this.cartSignature(fresh);
        live.dataset.mainInCart = 'true';
        live._cartPartnerIds = new Set((fresh?.items || cart?.items || before.items).map((item) => String(item.product_id)));
        live._cartPartnerIds.add(String(partnerId));
        live.querySelector('[data-pairing-another]').hidden = ![...live.querySelectorAll('[data-pairing-row]')].some((candidate) => !live._cartPartnerIds.has(candidate.dataset.partnerProductId));
        const template = mode === 'pair' ? this.dataset.addedTemplate : this.dataset.addedPartnerTemplate;
        const sentence = template.replace(/\[(main|partner)\]/g, (_token, field) => field === 'main' ? this.dataset.mainTitle : row.dataset.partnerTitle);
        live.querySelector('[data-pairing-confirmation-text]').textContent = sentence;
        const allowed = this.dataset.acceptedMainVariants || '|';
        const mainVariants = mode === 'pair' ? [this.dataset.mainVariantId] : mainBefore.map((item) => String(item.variant_id)).filter((id) => allowed === '|' || allowed.includes('|' + id + '|'));
        live._proof = { mainVariants, partnerId, variantId: row.dataset.variantId, rate: Number(this.dataset.rate) };
        live.querySelector('[data-pairing-confirmation-saving]').textContent = !postAddUnconfirmed && initialClaim && proofCurrent && fresh && bothAllocated(fresh, this.dataset.productId, mainVariants, partnerId, row.dataset.variantId, Number(this.dataset.rate)) ? this.dataset.appliedCopy : this.dataset.cartCopy;
        live.querySelector('[data-pairing-offer]').hidden = true;
        live.querySelector('[data-pairing-confirmation]').hidden = false;
        live.close();
        live.querySelector('[data-pairing-status]').textContent = sentence;
        live.querySelector('[data-pairing-confirmation] a')?.focus();
        ok = true;
        reason = postAddUnconfirmed ? 'added_unconfirmed' : tagged ? 'added' : 'added_without_tag';
      } catch (error) {
        reason = error.message || reason;
        if (reason === 'timeout') this._shared.needsReconcile = true;
        if (!attemptPublished) this.event('divano_pairing_add', { mode: 'unknown', partner_product_id: partnerId });
        const live = this._shared.current?.isConnected ? this._shared.current : this;
        if (reason === 'timeout') live.pending(true);
        live.querySelector('[data-pairing-claim]')?.replaceChildren(document.createTextNode(this.dataset.cartCopy));
        const status = live.querySelector('[data-pairing-status]');
        if (status) status.textContent = reason === 'variant_changed' ? '' : reason === 'rate_limited' ? this.dataset.rateCopy : reason === 'cart_line_limit' ? this.dataset.fullCopy : this.dataset.errorCopy;
      } finally {
        this._shared.adding = false;
        this._shared.current?.recalc();
        this.event('divano_pairing_add_result', { ok, reason });
      }
    }
  }

  customElements.define('divano-pdp-pairing', Pairing);
})();
